/** aria-label for both places the case's sections are listed: the rail and the toolbar's dropdown. */
export const CASE_SECTIONS_LIST_LABEL = "Case sections";

/** Shown for any field with no stored value, in every kind of control. */
export const EMPTY_VALUE_LABEL = "Not captured";

/**
 * Why a field the reviewer can see is not one they may change.
 *
 * <p>Deliberately one message rather than a per-field table: the document says which fields are
 * closed through `capabilities.fields`, and the screen's job is to say so, not to re-explain each
 * server rule in words that would drift from it.
 */
export const FIELD_NOT_EDITABLE_REASON = "This value is not editable here.";

/** The follow-up a note was filed under, shown as its chip subtitle. */
export const INITIAL_FOLLOWUP_TAG = "Initial (v0)";
export const followupVersionTag = (version: number): string => `Follow-up v${version}`;

/** Root of the TanStack Query key for one field's audit trail, so one open is one request. */
export const FIELD_HISTORY_QUERY_KEY = "catalog_field_history";

/** TanStack Query key for the installation's E2B profile, read once per session. */
export const E2B_PROFILE_QUERY_KEY = "e2b_installation_profile";

/** "Who changed it" labels, keyed by the audit trail's `actorKind`; an unrecognized kind reads as System. */
export const FIELD_HISTORY_ACTOR_LABELS: Record<string, string> = {
  HUMAN: "Person",
  AI_AGENT: "AI",
  SYSTEM: "System",
  INTEGRATION: "Integration",
};
export const FIELD_HISTORY_DEFAULT_ACTOR_LABEL = "System";
export const FIELD_HISTORY_UNKNOWN_STATE_NOTE = "previous value unknown";
export const FIELD_HISTORY_PII_LABEL = "Personal data";
export const FIELD_HISTORY_NOT_SET_LABEL = "not set";
export const FIELD_HISTORY_UNKNOWN_VALUE_LABEL = "—";
export const FIELD_HISTORY_CLEARED_LABEL = "cleared";
/** A masked PII value with no new display at all — still a change, just not one that names it. */
export const FIELD_HISTORY_PII_CHANGED_LABEL = "updated";
export const FIELD_HISTORY_LOADING_LABEL = "Loading history…";
export const FIELD_HISTORY_EMPTY_TITLE = "Never set.";
export const FIELD_HISTORY_EMPTY_DESCRIPTION = "Nothing has ever written this field.";
export const FIELD_HISTORY_ERROR_FALLBACK = "Could not load history.";

/** Accepting every outstanding AI value in the case, from the toolbar. */
export const ACCEPT_ALL_ACTION = "Accept all AI values";
export const ACCEPT_ALL_TITLE = "Accept all pending AI values?";
export const ACCEPT_ALL_CONFIRM = "Accept all";
export const ACCEPT_ALL_CANCEL = "Cancel";
export const ACCEPT_ALL_BODY =
  "Values you have already accepted or corrected are left as they are, and an accepted value can still be edited afterwards.";
/** Said plainly because the legacy modal said it, and reviewers asked: this is not finalisation. */
export const ACCEPT_ALL_NOT_FINAL =
  "Nothing is saved until you save, and this does not route or finalise the case.";
export const acceptAllPendingSummary = (count: number): string =>
  count === 1
    ? "This accepts the 1 AI value in this case that has not been reviewed."
    : `This accepts the ${count} AI values in this case that have not been reviewed.`;

/** Walking the case: the next thing still wanting attention, wherever it is. */
export const NEXT_UNREVIEWED_ACTION = "Next unreviewed";
export const NEXT_REQUIRED_ACTION = "Next required";
/** The footer notice when the walk finds nothing left (S7, issue 355). */
export const NEXT_UNREVIEWED_NONE_NOTICE = "No AI values are left to review.";
export const NEXT_REQUIRED_MESSAGES = {
  checking: "Checking requirements…",
  complete: "No outstanding requirements for the next transition.",
  saveFirst: "Save or cancel your changes before checking next-stage requirements.",
  blocked: "The next transition is blocked. Review the requirements below.",
} as const;
export const FOLLOWUP_PENDING_CHECKPOINT = "Follow-up changes reviewed";
export const FOLLOWUP_REVIEW_ACTION = "Review follow-up changes";
/** What the follow-up line of the Route window still waits for, counted per record and per value. */
export function followupPendingDetail(records: number, values: number): string {
  const parts: string[] = [];
  if (records > 0) parts.push(`${records} record${records === 1 ? "" : "s"} to answer`);
  if (values > 0) parts.push(`${values} value${values === 1 ? "" : "s"} to decide`);
  return parts.join(" · ");
}

/** What the reviewer is told before routing. */
export const CHECKPOINT_MANDATORY = "Stage gate checks";
export const CHECKPOINT_AI_REVIEWED = "All AI values reviewed";
export const CHECKPOINT_NARRATIVE = "Narrative written";
export const CHECKPOINT_COMMENTS = "Comments added";
/** One line under a failed checkpoint: what is wrong, how much of it, and where. */
export const CHECKPOINT_DETAIL_NARRATIVE_EMPTY = "Final Narrative is empty";
export const CHECKPOINT_DETAIL_COMMENTS_EMPTY = "No comment on this case";

/** "in Products" / "in Patient, Products and Event Details" — the sections, as the rail names them. */
function inSections(sections: string[]): string {
  if (!sections.length) return "";
  const list = sections.length === 1 ? sections[0]
    : `${sections.slice(0, -1).join(", ")} and ${sections[sections.length - 1]}`;
  return ` in ${list}`;
}

export const routeDetailRequired = (count: number, sections: string[]): string =>
  count === 0 ? "Resolve the stage gate findings below."
    : `${count} ${count === 1 ? "finding" : "findings"} to resolve${inSections(sections)}`;

export const routeDetailUnreviewed = (count: number, sections: string[]): string =>
  `${count} AI ${count === 1 ? "value" : "values"} not reviewed${inSections(sections)}`;

export const ROUTE_MODAL_TITLE = "Route to the next stage";
export const ROUTE_CHECKING_NOTICE = "Checking the complete saved case…";
export const ROUTE_MODAL_CONFIRM = "Route";
export const ROUTE_MODAL_CANCEL = "Cancel";
export const ROUTE_REASON_LABEL = "Route comment";
export const ROUTE_REASON_REQUIRED = "A route comment is required.";
export const ROUTE_BLOCKED_NOTICE =
  "Resolve all blocking findings before routing.";
export const ROUTE_ADVISORY_NOTICE =
  "These are worth checking, but they do not stop the case moving on.";
/** The action on each failed checkpoint row: where it sends the reviewer. */
export const ROUTE_ACTION_FIRST_REQUIRED = "Go to the first one";
export const ROUTE_ACTION_SHOW_UNREVIEWED = "Show unreviewed";
export const ROUTE_ACTION_NARRATIVE = "Go to narrative";
export const ROUTE_ACTION_COMMENTS = "Go to comments";

/** The missing-fields panel: a refusal when routing is blocked, a warning when it is not. */
export const ROUTE_MISSING_BLOCKED_TITLE = "Cannot route yet";
export const ROUTE_MISSING_ADVISORY_TITLE = "Items to review";
export const ROUTE_MISSING_HINT = "Select a field to go straight to it.";
/** What a finding's message starts with, by severity. An error needs none; an info only tells. */
export const ROUTE_FINDING_PREFIX: Readonly<Record<string, string>> = {
  WARNING: "Warning: ",
  INFO: "Note: ",
};

/** Between the two stage names in the modal's subtitle. */
export const ROUTE_STAGE_ARROW = "→";
/** Between a field and the subsection it sits in, in the missing-fields list. */
export const ROUTE_FIELD_GROUP_SEPARATOR = "·";

export const routeConfirmLabel = (nextStage: string): string => `Route to ${nextStage}`;
/** Why the primary button is disabled, as its tooltip. */
export const ROUTE_DISABLED_BLOCKED = "Resolve all blocking findings before routing.";
export const ROUTE_DISABLED_NO_COMMENT = "A route comment is required.";
export const ROUTE_DISABLED_NOT_READY = "Check the saved case before routing.";
export const ROUTE_REASON_PLACEHOLDER = "Add a routing comment (required)…";

export const ROUTE_FIX_THIS = "Fix this";

// ── Review counts (ReviewCounter / ReviewCountDetails) ──────────────────────
export const REVIEW_COUNT_NONE = "—";
export const REVIEW_COUNT_REQUIRED_HEADING = "Required fields";
export const REVIEW_COUNT_AI_HEADING = "AI values";
export const REVIEW_COUNT_FOLLOWUP_HEADING = "Follow-up proposals";
export const REVIEW_COUNT_RECORDS_HEADING = "Follow-up records";
export const REVIEW_COUNT_RECORDS_WHERE = "Match or add each one in Follow-up changes.";
export const REVIEW_COUNT_ACCEPTED = "Accepted";
export const REVIEW_COUNT_CORRECTED = "Corrected";
export const REVIEW_COUNT_REJECTED = "Rejected";
export const REVIEW_COUNT_TO_REVIEW = "To review";
export const REVIEW_COUNT_MANUAL_ENTRY = "Needs manual entry";
export const REVIEW_COUNT_ALL_DONE = "Nothing left here";
export const REVIEW_COUNT_NOTHING = "No AI values or required fields here";
export const REVIEW_COUNT_MISSING_SHOWN = 5;
export const reviewCountFilled = (filled: number, total: number): string => `${filled} of ${total} filled`;
export const reviewCountReviewed = (reviewed: number, total: number): string => `${reviewed} of ${total} reviewed`;
export const reviewCountDecided = (decided: number, total: number): string => `${decided} of ${total} decided`;
export const reviewCountRequiredEmpty = (count: number): string => `${count} required ${count === 1 ? "field" : "fields"} empty`;
export const reviewCountAiLeft = (count: number): string => `${count} AI ${count === 1 ? "value" : "values"} to review`;
export const reviewCountManualLeft = (count: number): string =>
  `${count} ${count === 1 ? "field needs" : "fields need"} manual entry`;
export const reviewCountResolved = (resolved: number, total: number): string => `${resolved} of ${total} resolved`;
export const reviewCountRecordsLeft = (count: number): string => `${count} incoming ${count === 1 ? "record" : "records"} to resolve`;
export const reviewCountFollowupLeft = (count: number): string => `${count} follow-up ${count === 1 ? "proposal" : "proposals"} pending`;
export const reviewCountMoreMissing = (count: number): string => `and ${count} more`;
export const REVIEW_COUNT_MISSING_PREFIX = "Empty:";

/**
 * A field whose extracted text could not be used as the field's type (#81): nothing to accept, so
 * the reviewer enters the value. Accept stays visible but disabled, with this as its tooltip.
 */
export const MANUAL_ENTRY_ACCEPT_TITLE = "The extracted value could not be used; enter it manually.";
/** The warning marker's accessible name and tooltip beside such a field's confidence pill. */
export const MANUAL_ENTRY_FLAG_LABEL = "Could not be extracted — see reason";

/** The line under a field whose extracted value was too long to store (issue #400). */
export const tooLongValueNote = (length: number, limit: number): string =>
  `The value read from the document is too long to store (${length} characters; the limit is ${limit}). `
  + "Enter a shorter value or leave the field empty.";
/** The route checkpoint that lists those fields; it reminds and never blocks. */
export const TOO_LONG_CHECKPOINT = "Values too long to store";
export const ROUTE_ACTION_TOO_LONG = "Go to the first field";
export const routeDetailTooLong = (count: number, sections: string[]): string =>
  `${count} ${count === 1 ? "value" : "values"} left empty${inSections(sections)}`;

/** A case the server will not let anyone edit, such as a follow-up source joined to its original (issue #47). */
export const CASE_READ_ONLY_NOTE = "This case is read-only.";
/** The pop-up after a successful Save; the footer's notice says the same, less visibly. */
export const CASE_REVIEW_SAVED_TOAST = "Changes saved";

export const WHODD_CODE_FIELD_ID = "case.product.products.drug_code";
export const PRODUCT_TYPE_KEY = "product_type";
export const DEVICE_PRODUCT_TYPE_LABEL = "device";
export const WHODD_OUT_OF_SCOPE_LABEL = "Out of scope";
/** Why Save is disabled while Theragenie's proposals wait for a decision; also the footer note. */
export const proposalsPendingReason = (count: number): string =>
  `${count} ${count === 1 ? "proposal" : "proposals"} pending — accept or reject each one before saving.`;
/** The finding a pending proposal raises when Save is pressed anyway; it sends the reviewer to the field. */
export const PROPOSAL_PENDING_FINDING_ID = "proposal-pending";
export const PROPOSAL_PENDING_FINDING_MESSAGE = "Accept or reject Theragenie's proposal before saving.";
/** The pill on a proposed row, and the disclosure behind it. */
export const PROPOSAL_PILL_TITLE = "Proposed by Theragenie · why?";
export const PROPOSAL_ACCEPT_TITLE = "Accept the proposed value";
export const PROPOSAL_REJECT_TITLE = "Reject the proposed value and restore the previous one";
export const PROPOSAL_UNDO_TITLE = "Step back to the previous version";
export const PROPOSAL_REDO_TITLE = "Step forward to the version undone";
/** Why a proposal sharing only some fields with a pending one is refused; the agent relays it. */
export const proposalOverlapReason = (pendingFieldIds: string[]): string =>
  `Decide Theragenie's pending proposal on ${pendingFieldIds.join(", ")} first: a new proposal must set the same fields or none of them.`;
/** The history label of the value the agent proposed, beside the alternatives it named. */
export const PROPOSAL_VERSION_PROPOSED = "Proposed";
/** "Version 2 of 3 · Alt 2: Headache" — where a pending proposal stands in its history; shown only when there is more than one version. */
export const proposalVersionText = (version: { index: number; length: number; label: string }): string =>
  `Version ${version.index} of ${version.length - 1}${version.label ? ` · ${version.label}` : ""}`;
export const hasProposalVersions = (version: { length: number }): boolean => version.length > 2;

/** Why a field's Theragenie action button cannot be used right now. */
export const FIELD_ACTION_NEEDS_EDIT_REASON = "Press Edit case to use this action.";
export const FIELD_ACTION_NO_DOCK_REASON = "Theragenie is not available on this screen.";
/** What a field's Theragenie button shows while its action runs. */
export const FIELD_ACTION_WORKING = "Theragenie is working…";
/** "Write narrative for Final Narrative with Theragenie" — the button's accessible name. */
export const fieldActionName = (actionLabel: string, fieldLabel: string): string => `${actionLabel} for ${fieldLabel} with Theragenie`;
export const proposalUndoName = (fieldLabel: string): string => `Undo Theragenie proposal on ${fieldLabel}`;
export const proposalRedoName = (fieldLabel: string): string => `Redo Theragenie proposal on ${fieldLabel}`;

/** Coded indication is an intake fact for review, never a WHODD matching input. */
export const INTAKE_CODED_INDICATION_LABEL = "Coded indication at intake";

/** An extraction review can exist without a graded confidence score. */
export const AI_CONFIDENCE_NOT_PROVIDED_LABEL = "Not provided";
